import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HeartPulse, RefreshCw, Loader2, TrendingUp, TrendingDown, Sparkles, AlertTriangle } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const VERDICT_TONE = {
  healthy: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  strained: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  critical: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};
const SEV_TONE = {
  low: "border-amber-500/30 text-amber-300",
  medium: "border-orange-500/30 text-orange-300",
  high: "border-rose-500/30 text-rose-300",
};
const SENT_COLORS = { positive: "#10b981", neutral: "#71717a", negative: "#f43f5e" };

export default function SentimentPulsePage() {
  const { propertyId } = useProperty();
  const [days, setDays] = useState(30);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/admin/ai/sentiment/pulse", {
        params: { property_id: propertyId || "all", days },
      });
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  }, [propertyId, days]);

  useEffect(() => { load(); }, [load]);

  const score = result?.health_score;
  const src = result?._source || {};
  const dist = result?.sentiment_distribution;
  const pie = dist ? [
    { name: "positive", value: dist.positive || 0 },
    { name: "neutral", value: dist.neutral || 0 },
    { name: "negative", value: dist.negative || 0 },
  ] : [];

  return (
    <div>
      <PageHeader
        eyebrow="AI Features"
        title="Sentiment Pulse"
        subtitle="Claude sweeps recent tickets, timeline notes, and broadcasts to surface community health before it becomes a board issue."
        actions={
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="border-white/10 h-9 w-32" data-testid="pulse-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} disabled={busy} variant="outline" className="border-white/10" data-testid="pulse-refresh">
              <RefreshCw className={"h-3 w-3 mr-1 " + (busy ? "animate-spin" : "")} /> {busy ? "Sweeping…" : "Refresh"}
            </Button>
          </div>
        }
      />

      {busy && !result && (
        <Card className="border-white/10 bg-card p-16" data-testid="pulse-loading">
          <div className="flex items-center justify-center gap-2 text-sm text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Claude is scanning {src.analyzed?.tickets || "…"} tickets and reading between the lines…
          </div>
        </Card>
      )}

      {result && (
        <div className="space-y-4" data-testid="pulse-result">
          {/* Big score card */}
          <Card className={"border p-5 " + (VERDICT_TONE[result.verdict] || "border-white/10")} data-testid="pulse-score">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <HeartPulse className="h-8 w-8" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80">Community health</div>
                  <div className="text-5xl font-bold font-display tabular-nums leading-none mt-1" data-testid="pulse-score-num">{score ?? "—"}<span className="text-lg opacity-60">/100</span></div>
                  <div className="text-sm opacity-90 mt-1 max-w-xl" data-testid="pulse-headline">{result.headline}</div>
                </div>
              </div>
              <Badge variant="outline" className="border-white/20 text-[10px] uppercase tracking-widest">
                {result.verdict}
              </Badge>
            </div>
            <div className="text-[10px] text-zinc-500 mt-3 border-t border-white/5 pt-2">
              Analyzed {src.analyzed?.tickets || 0} tickets · {src.analyzed?.notes || 0} timeline notes · {src.analyzed?.notifications || 0} broadcasts over the last {src.window_days || days} days.
            </div>
          </Card>

          {/* Distribution + top complaints */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-white/10 bg-card p-5 lg:col-span-1" data-testid="pulse-distribution">
              <div className="overline mb-2">Sentiment distribution</div>
              {pie.length > 0 && pie.some((p) => p.value > 0) ? (
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pie} innerRadius={40} outerRadius={80} paddingAngle={2} dataKey="value">
                        {pie.map((p) => <Cell key={p.name} fill={SENT_COLORS[p.name]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v) => `${v}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-xs text-zinc-500 italic p-4">Distribution unavailable.</div>
              )}
              <div className="flex justify-around text-xs mt-2">
                <div className="text-emerald-300">● positive {dist?.positive ?? 0}%</div>
                <div className="text-zinc-400">● neutral {dist?.neutral ?? 0}%</div>
                <div className="text-rose-300">● negative {dist?.negative ?? 0}%</div>
              </div>
            </Card>

            <Card className="border-white/10 bg-card p-5 lg:col-span-2" data-testid="pulse-complaints">
              <div className="overline mb-3">Top complaints</div>
              {(result.top_complaints || []).length === 0 && (
                <div className="text-xs text-zinc-500 italic">No recurring complaints detected — good sign!</div>
              )}
              <div className="space-y-2">
                {(result.top_complaints || []).map((c, i) => (
                  <div key={`${c.theme}-${i}`} className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid={`pulse-complaint-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-medium">{c.theme}</div>
                      <Badge variant="outline" className={SEV_TONE[c.severity] || "border-white/10"}>{c.severity}</Badge>
                      <Badge variant="outline" className="border-white/10 text-[10px] text-zinc-400">~{c.count_est} mentions</Badge>
                    </div>
                    {c.sample_quote && (
                      <div className="text-xs text-zinc-400 italic mt-1 border-l-2 border-white/10 pl-2">&ldquo;{c.sample_quote}&rdquo;</div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Signals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {result.emerging_signals?.length > 0 && (
              <Card className="border-amber-500/20 bg-amber-500/[0.03] p-5" data-testid="pulse-emerging">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" /> Emerging concerns
                </div>
                <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                  {result.emerging_signals.map((s, i) => <li key={`emerging-${i}-${(s || "").slice(0, 32)}`}>{s}</li>)}
                </ul>
              </Card>
            )}
            {result.positive_signals?.length > 0 && (
              <Card className="border-emerald-500/20 bg-emerald-500/[0.03] p-5" data-testid="pulse-positive">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Positive signals
                </div>
                <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                  {result.positive_signals.map((s, i) => <li key={`positive-${i}-${(s || "").slice(0, 32)}`}>{s}</li>)}
                </ul>
              </Card>
            )}
          </div>

          {result.recommendations?.length > 0 && (
            <Card className="border-indigo-500/20 bg-indigo-500/[0.03] p-5" data-testid="pulse-recs">
              <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-2 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Recommended actions
              </div>
              <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                {result.recommendations.map((r, i) => <li key={`rec-${i}-${(r || "").slice(0, 32)}`} data-testid={`pulse-rec-${i}`}>{r}</li>)}
              </ul>
            </Card>
          )}

          {result.error && (
            <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/5 p-3 rounded-md flex items-start gap-2" data-testid="pulse-error">
              <AlertTriangle className="h-3 w-3 mt-0.5" />
              <div>Claude returned unparseable JSON. Try again or narrow the window.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
